import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentGame } from '@/store/gameSlice';
import { selectUser } from '@/store/authSlice';
import type { Player, Resources } from '@/types';
import { usePlayerIcons } from '@/hooks/usePlayerIcons';
import './PlayerPanel.css';

// Resource icon cache configuration (reuse from ResourcePanel)
const RESOURCE_CACHE_PREFIX = 'rush-icon-';
const RESOURCE_CACHE_VERSION = '2'; // Incremented to clear corrupted data URI cache
const RESOURCE_CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Resource type to icon filename mapping
const RESOURCE_ICON_MAP: Record<string, string> = {
  energy: 'energy-8bit.png',
  minerals: 'minerals-8bit.png',
  food: 'food-8bit.png',
  oxygen: 'oxygen-8bit.png',
  hydrogen: 'hydrogen-8bit.png',
  water: 'water-8bit.png',
  helium3: 'helium3-8bit.png',
  rovers: 'rovers-8bit.png',
  money: 'money-8bit.png',
  land: 'land-8bit.png',
};

/**
 * Get cached resource icon
 */
const getCachedResourceIcon = (iconName: string): string | null => {
  try {
    const cacheKey = `${RESOURCE_CACHE_PREFIX}${iconName}-v${RESOURCE_CACHE_VERSION}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    const { dataUri, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    if (now - timestamp > RESOURCE_CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return dataUri;
  } catch (error) {
    return null;
  }
};

/**
 * Cache resource icon
 */
const cacheResourceIcon = (iconName: string, dataUri: string): void => {
  try {
    const cacheKey = `${RESOURCE_CACHE_PREFIX}${iconName}-v${RESOURCE_CACHE_VERSION}`;
    const cacheData = {
      dataUri,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    // localStorage might be full
  }
};

/**
 * Calculate net worth based on resources and market prices
 */
const calculateNetWorth = (resources: Resources, marketPrices: Record<string, number>): number => {
  const resourceValues: Record<string, number> = {
    energy: resources.energy * (marketPrices.energy || 0),
    minerals: resources.minerals * (marketPrices.minerals || 0),
    food: (resources.food ?? 0) * (marketPrices.food || 0),
    oxygen: (resources.oxygen ?? 0) * (marketPrices.oxygen || 0),
    hydrogen: (resources.hydrogen ?? 0) * (marketPrices.hydrogen || 0),
    water: (resources.water ?? 0) * (marketPrices.water || 0),
    helium3: (resources.helium3 ?? 0) * (marketPrices.helium3 || 0),
    rovers: (resources.rovers ?? 0) * (marketPrices.rovers || 0),
  };
  
  return Object.values(resourceValues).reduce((sum, value) => sum + value, 0);
};

/**
 * PlayerPanel - Displays all players with their resources
 * 
 * Inspired by original M.U.L.E. game which showed all 4 players
 * with their resources in a dedicated panel.
 */
export default function PlayerPanel() {
  const game = useSelector(selectCurrentGame);
  const currentUser = useSelector(selectUser);
  const playerIcons = usePlayerIcons();
  const [resourceIcons, setResourceIcons] = useState<Record<string, string>>({});
  
  const players = game?.players || [];
  const turnPlayerId = game?.phase === 'development' ? game.developmentTimer?.currentPlayerId : null;

  // Load resource icons on mount
  useEffect(() => {
    const loadResourceIcons = async () => {
      const resourceTypes = Object.keys(RESOURCE_ICON_MAP);
      const loadedIcons: Record<string, string> = {};
      
      for (const resourceType of resourceTypes) {
        const iconName = RESOURCE_ICON_MAP[resourceType];
        
        // Check cache first
        const cachedDataUri = getCachedResourceIcon(iconName);
        
        if (cachedDataUri) {
          loadedIcons[resourceType] = cachedDataUri;
          continue;
        }
        
        // Cache miss - fetch from get_icon API (S3 via Lambda)
        try {
          const { apiClient } = await import('@/services/api');
          const dataUrl = await apiClient.getIcon(iconName);
          
          loadedIcons[resourceType] = dataUrl;
          cacheResourceIcon(iconName, dataUrl);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`✅ Fetched and cached resource icon from API: ${iconName}`);
          }
        } catch (error) {
          console.error(`Error loading resource icon ${iconName}:`, error);
        }
      }
      
      setResourceIcons(loadedIcons);
    };
    
    loadResourceIcons();
  }, []);
  
  // Ensure we show up to 4 players (pad with empty slots if needed)
  const displayPlayers: (Player | null)[] = [...players];
  while (displayPlayers.length < 4) {
    displayPlayers.push(null);
  }
  
  return (
    <div className="player-panel">
      <h3 className="player-panel-title">PLAYERS</h3>
      <div className="player-list">
        {displayPlayers.map((player, index) => {
          if (!player) {
            return (
              <div key={`empty-${index}`} className="player-slot empty">
                <div className="player-icon-container">
                  <div className="player-icon-placeholder">?</div>
                </div>
                <div className="player-info">
                  <div className="player-name">---</div>
                  <div className="player-resources">Waiting...</div>
                </div>
              </div>
            );
          }
          
          const isCurrentPlayer = player.playerId === currentUser?.userId;
          const isCurrentTurn = !!turnPlayerId && player.playerId === turnPlayerId;
          const resources: Resources = player.resources || {
            energy: 0,
            minerals: 0,
            food: 0,
            oxygen: 0,
            hydrogen: 0,
            water: 0,
            helium3: 0,
          };
          
          // Get player skills (from server)
          const skills = player.skills || {
            mining: 50,
            farming: 50,
            solarFarming: 50,
            chemist: 50,
            luck: 5,
          };
          
          // Create tooltip text with skills
          const skillsTooltip = `Skills:\nMining: ${skills.mining}\nFarming: ${skills.farming}\nSolar: ${skills.solarFarming}\nChemist: ${skills.chemist}\nLuck: ${skills.luck}%`;
          
          // Calculate land ownership statistics
          const mapPlots = game?.map?.plots || [];
          // Check both ownerId (old) and claimedBy.playerId (new)
          const ownedPlots = mapPlots.filter(plot => 
            plot.ownerId === player.playerId || plot.claimedBy?.playerId === player.playerId
          );
          const totalOwned = ownedPlots.length;
          const cultivatedPlots = ownedPlots.filter(plot => plot.robotId != null && plot.robotId !== '');
          const totalCultivated = cultivatedPlots.length;
          const utilizationPercent = totalOwned > 0 ? Math.round((totalCultivated / totalOwned) * 100) : 0;
          
          // Create land tooltip text
          const landTooltip = `Land Ownership:\nTotal Parcels: ${totalOwned}\nCultivated: ${totalCultivated}\nUtilization: ${utilizationPercent}%`;
          
          return (
            <div
              key={player.playerId}
              className={`player-slot ${isCurrentPlayer ? 'current' : ''} ${isCurrentTurn ? 'active-turn' : ''}`}
            >
              <div className="player-icon-container" data-tooltip={skillsTooltip}>
                {playerIcons[player.playerNumber] ? (
                  <img
                    src={playerIcons[player.playerNumber]}
                    alt={`Player ${player.playerNumber}`}
                    className="player-icon"
                  />
                ) : (
                  <div className="player-icon-placeholder">
                    {player.playerNumber}
                  </div>
                )}
              </div>
              <div className="player-info">
                <div className="player-name">
                  {player.playerName || player.username || `Player ${player.playerNumber}`}
                  {isCurrentPlayer && <span className="player-you"> (YOU)</span>}
                  {isCurrentTurn && <span className="player-turn-badge">TURN</span>}
                </div>
                <div className="player-resources">
                  <div className="resource-row">
                    <div className="resource-item" data-tooltip={`Energy: ${resources.energy}`}>
                      {resourceIcons.energy ? (
                        <img src={resourceIcons.energy} alt="Energy" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">🔋</span>
                      )}
                      <span className="resource-value" data-tooltip={`Energy: ${resources.energy}`}>{resources.energy}</span>
                    </div>
                    <div className="resource-item" data-tooltip={`Minerals: ${resources.minerals}`}>
                      {resourceIcons.minerals ? (
                        <img src={resourceIcons.minerals} alt="Minerals" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">⛏️</span>
                      )}
                      <span className="resource-value" data-tooltip={`Minerals: ${resources.minerals}`}>{resources.minerals}</span>
                    </div>
                    <div className="resource-item" data-tooltip={`Food: ${resources.food ?? 0}`}>
                      {resourceIcons.food ? (
                        <img src={resourceIcons.food} alt="Food" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">🌾</span>
                      )}
                      <span className="resource-value" data-tooltip={`Food: ${resources.food ?? 0}`}>{resources.food ?? 0}</span>
                    </div>
                  </div>
                  <div className="resource-row">
                    <div className="resource-item" data-tooltip={`Oxygen: ${resources.oxygen ?? 0}`}>
                      {resourceIcons.oxygen ? (
                        <img src={resourceIcons.oxygen} alt="Oxygen" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">💨</span>
                      )}
                      <span className="resource-value" data-tooltip={`Oxygen: ${resources.oxygen ?? 0}`}>{resources.oxygen ?? 0}</span>
                    </div>
                    <div className="resource-item" data-tooltip={`Hydrogen: ${resources.hydrogen ?? 0}`}>
                      {resourceIcons.hydrogen ? (
                        <img src={resourceIcons.hydrogen} alt="Hydrogen" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">☁️</span>
                      )}
                      <span className="resource-value" data-tooltip={`Hydrogen: ${resources.hydrogen ?? 0}`}>{resources.hydrogen ?? 0}</span>
                    </div>
                    <div className="resource-item" data-tooltip={`Water: ${resources.water ?? 0}`}>
                      {resourceIcons.water ? (
                        <img src={resourceIcons.water} alt="Water" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">💧</span>
                      )}
                      <span className="resource-value" data-tooltip={`Water: ${resources.water ?? 0}`}>{resources.water ?? 0}</span>
                    </div>
                  </div>
                  <div className="resource-row">
                    <div className="resource-item" data-tooltip={`Helium-3: ${resources.helium3 ?? 0}`}>
                      {resourceIcons.helium3 ? (
                        <img src={resourceIcons.helium3} alt="Helium-3" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">✨</span>
                      )}
                      <span className="resource-value" data-tooltip={`Helium-3: ${resources.helium3 ?? 0}`}>{resources.helium3 ?? 0}</span>
                    </div>
                    <div className="resource-item" data-tooltip={landTooltip}>
                      {resourceIcons.land ? (
                        <img src={resourceIcons.land} alt="Land" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">🗺️</span>
                      )}
                      <span className="resource-value" data-tooltip={landTooltip}>{totalOwned}</span>
                    </div>
                    <div className="resource-item" data-tooltip={`Cyber Tokens: ${player.money.toLocaleString()}`}>
                      {resourceIcons.money ? (
                        <img src={resourceIcons.money} alt="Cyber Tokens" className="resource-icon-small" />
                      ) : (
                        <span className="resource-icon-fallback">🪙</span>
                      )}
                      <span className="resource-value money" data-tooltip={`Cyber Tokens: ${player.money.toLocaleString()}`}>{player.money.toLocaleString()}</span>
                    </div>
                  </div>
                  {/* Net Worth */}
                  <div className="player-net-worth">
                    <div className="net-worth-label">Net Worth:</div>
                    <div className="net-worth-value">
                      {(player.money + calculateNetWorth(resources, game?.market?.prices || {})).toLocaleString()} CT
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

